import {
  Grid2 as Grid,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import ClearIcon from "@mui/icons-material/Clear"; // Import the ClearIcon

export const SalesUsers = ({ title, staffRows, setSalesData }) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const [selectedRow, setSelectedRow] = useState(null);
  const [changeValue, setChangeValue] = useState("");
  const [newCellText, setNewCellText] = useState("");
  const [removeLoading, setRemoveLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [tempToken, setTempToken] = useState({
    email: "",
    token: "",
  });
  const [values, setValues] = useState({
    fullName: "",
    email: "",
  });

  useEffect(() => {
    const clearTextInterval = setInterval(() => {
      if (errorText !== "") {
        setErrorText("");
      }
    }, 3000);

    return () => {
      clearInterval(clearTextInterval); // Clear the interval when the component unmounts
    };
  }, [errorText]);

  useEffect(() => {
    if (tempToken?.token && tempToken?.email) {
      // Find the correct staff member in staffRows
      const updatedStaffRows = staffRows.map(
        (staff) =>
          staff.email === tempToken.email
            ? { ...staff, token: tempToken.token } // Update the token if email matches
            : staff // Keep others unchanged
      );

      // Update the sales data
      setSalesData((prevData) => ({
        ...prevData,
        users: updatedStaffRows, // Replace admin_registration with updated list
      }));
      setTempToken({ email: "", token: "" }); // Clear the temp token
    }
  }, [tempToken, staffRows, setSalesData]);

  const handleEdit = async (type) => {
    const payload = {
      submitType: type,
      selectedRow: selectedRow,
      changeValue: changeValue,
      addValues: values,
    };

    try {
      if (type === "remove") {
        setRemoveLoading(true);
      } else if (type === "edit") {
        setEditLoading(true);
      } else if (type === "add") {
        setAddLoading(true);
      }

      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users/manage`,
        {
          payload,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      const token = resp?.data?.token;

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/sales/users`,
        { currentSession: currentSession },
        { withCredentials: true }
      );

      setSalesData(res.data.data);

      if (token) {
        setTempToken({ email: newCellText, token: token });
      }

      setErrorText(resp.data.message);

      if (type === "remove") {
        setRemoveLoading(false);
      } else if (type === "edit") {
        setEditLoading(false);
      } else if (type === "add") {
        setAddLoading(false);
      }

      setSelectedRow(null);
      setChangeValue("");
      setValues({ fullName: "", email: "" });
    } catch (e) {
      setErrorText(e.response.data.message);
      if (type === "remove") {
        setRemoveLoading(false);
      } else if (type === "edit") {
        setEditLoading(false);
      } else if (type === "add") {
        setAddLoading(false);
      }
    }
  };

  const handleRowClick = (params) => {
    setSelectedRow(params?.row);
    setChangeValue(params?.row?.email);
  };

  const staffColumns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "added_by",
      headerName: "Added By",
      flex: 1,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "date",
      headerName: "Date",
      flex: 0.5,
      width: "100%",
      headerClassName: "management-datagrid-title",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      width: "100%",
      headerClassName: "management-datagrid-title",
      valueGetter: (params) => {
        return params?.row?.status === 0 ? "Invited" : "Active";
      },
    },
  ];

  return (
    <Card sx={{ minHeight: "35rem" }}>
      <CardHeader title={title} />
      <CardContent>
        <DataGrid
          sx={{
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            minHeight: "24rem",
          }}
          rows={staffRows}
          columns={staffColumns}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25, 50, 100]}
          onRowClick={handleRowClick}
          classes={{
            cell: "cell", // Apply custom cell class
          }}
        />
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {selectedRow && (
                <>
                  Selected User -{" "}
                  <span style={{ fontWeight: "normal" }}>
                    {selectedRow.email}
                  </span>
                </>
              )}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              disabled={selectedRow?.is_registered || !selectedRow}
              label="Edit/Remove User"
              variant="filled"
              fullWidth
              value={changeValue || ""}
              onChange={(e) => setChangeValue(e.target.value)}
              slotProps={{
                inputs: {
                  endAdornment: selectedRow ? (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={
                          !selectedRow ||
                          removeLoading ||
                          addLoading ||
                          editLoading
                        }
                        onClick={() => {
                          setChangeValue("");
                          setSelectedRow(null);
                        }}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <LoadingButton
              loading={editLoading}
              disabled={
                !selectedRow ||
                !changeValue ||
                changeValue === selectedRow?.email ||
                removeLoading ||
                addLoading
              }
              fullWidth
              variant="contained"
              color="warning"
              onClick={() => {
                handleEdit("edit");
              }}
            >
              Edit
            </LoadingButton>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <LoadingButton
              loading={removeLoading}
              disabled={!selectedRow || editLoading || addLoading}
              fullWidth
              variant="contained"
              color="error"
              onClick={() => {
                handleEdit("remove");
              }}
            >
              Remove
            </LoadingButton>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="body1"
              color="primary"
              fontWeight="bold"
              textAlign="center"
              sx={{ mt: "1rem", minHeight: "1.5rem" }}
            >
              Add New User
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              disabled={addLoading}
              label={"Full Name"}
              variant="filled"
              fullWidth
              value={values?.fullName || ""}
              onChange={(e) =>
                setValues({ ...values, fullName: e.target.value })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <TextField
              disabled={addLoading}
              label={"Email"}
              variant="filled"
              fullWidth
              value={values?.email || ""}
              onChange={(e) => setValues({ ...values, email: e.target.value })}
            />
          </Grid>

          <Grid isize={{ xs: 12, md: 2 }}>
            <LoadingButton
              loading={addLoading}
              disabled={
                !values?.email ||
                !values?.fullName ||
                removeLoading ||
                editLoading
              }
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => {
                handleEdit("add");
              }}
            >
              Add New
            </LoadingButton>
          </Grid>
        </Grid>
        <Typography
          variant="body1"
          color="primary"
          fontWeight="bold"
          textAlign="center"
          sx={{ mt: "1rem", minHeight: "1.5rem" }}
        >
          {errorText}
        </Typography>
      </CardContent>
    </Card>
  );
};
